import { faCircleUser } from '@fortawesome/free-solid-svg-icons/faCircleUser';
import React from 'react';
import styled from 'styled-components';

import { AtiraIcon } from '../../../../components/AtiraIcon';
import { AtiraLink } from '../../../../model/atira-link/AtiraLink';
import { AtiraLinkLanguageStrings } from '../../../../model/atira-link/AtiraLinkLangStrings';
import { CreateAtiraLinkDto } from '../../../../model/atira-link/dto/CreateAtiraLinkDto';
import { EditAtiraLinkDto } from '../../../../model/atira-link/dto/EditAtiraLinkDto';
import { TemplatePreviewMode } from '../../../shared/model/TemplatePreviewMode.enum';
import { getLinkImage } from '../../utils/getLinkImage';
import {
  getCustomizableButtonStyles,
  getCustomizableImageStyles,
} from '../../utils/styleOptions';

const PhotoWrapper = styled.div`
  svg {
    width: 100%;
    height: 100%;
  }
`;

const StyledLink = styled.a`
  height: 2.7rem;
`;

type AtiraLinkLeftSideProps = {
  atiraLink?:
    | AtiraLink
    | CreateAtiraLinkDto
    | EditAtiraLinkDto
    | Partial<AtiraLink>;
  linkLanguageStrings: AtiraLinkLanguageStrings;
  previewMode?: TemplatePreviewMode;
  showCustomStyles?: boolean;
};

export const AtiraLinkLeftSide: React.FC<AtiraLinkLeftSideProps> = ({
  atiraLink,
  linkLanguageStrings,
  previewMode = TemplatePreviewMode.MOBILE,
  showCustomStyles = true,
}) => {
  return (
    <div
      className={`atira-card ${previewMode === TemplatePreviewMode.DESKTOP ? 'p-4' : 'p-2'} d-flex flex-column gap-3 align-items-center h-100`}
    >
      <div
        className="user-pfp overflow-hidden"
        style={
          showCustomStyles
            ? getCustomizableImageStyles(atiraLink?.styles?.image)
            : undefined
        }
      >
        {atiraLink?.image ? (
          <img
            src={getLinkImage(atiraLink)}
            alt={`${atiraLink?.title}-image`}
            className="w-100 h-100 d-inline-block object-fit-cover"
          />
        ) : (
          <PhotoWrapper className="w-100 h-100 d-inline-block">
            <AtiraIcon icon={faCircleUser} />
          </PhotoWrapper>
        )}
      </div>

      <h1 className="title text-break">{atiraLink?.title}</h1>

      {atiraLink?.bio ? (
        <p className="bio text-center text-break">{atiraLink?.bio}</p>
      ) : null}

      <div className="row justify-content-center align-items-center g-2 w-100 left-side-row">
        {atiraLink?.email?.value ? (
          <div
            className={`${previewMode === TemplatePreviewMode.DESKTOP ? 'col-6' : 'col-12'} ${previewMode !== TemplatePreviewMode.MOBILE ? 'gx-1' : 'gx-0'} gy-1`}
          >
            <StyledLink
              href={atiraLink.email?.value}
              style={
                showCustomStyles
                  ? getCustomizableButtonStyles(atiraLink.styles?.button)
                  : undefined
              }
              className="social-link gap-2 py-2 px-1 w-100 d-flex align-items-center justify-content-center text-decoration-none"
            >
              <div className="d-flex align-items-center justify-content-center">
                <i className="fa-solid fa-envelope"></i>
              </div>
              <p className="m-0 text-truncate">{atiraLink.email?.label}</p>
            </StyledLink>
          </div>
        ) : null}

        {atiraLink?.website?.value ? (
          <div
            className={`${previewMode === TemplatePreviewMode.DESKTOP ? 'col-6' : 'col-12'} ${previewMode !== TemplatePreviewMode.MOBILE ? 'gx-1' : 'gx-0'} gy-1`}
          >
            <StyledLink
              href={
                atiraLink.website?.value.startsWith('http')
                  ? atiraLink.website.value
                  : `https://${atiraLink.website.value}`
              }
              style={
                showCustomStyles
                  ? getCustomizableButtonStyles(atiraLink.styles?.button)
                  : undefined
              }
              className="social-link gap-2 py-2 px-1 w-100 d-flex align-items-center justify-content-center text-decoration-none"
            >
              <div className="d-flex align-items-center justify-content-center">
                <i className="fa-solid fa-globe"></i>
              </div>
              <p className="m-0 text-truncate">{atiraLink.website?.label}</p>
            </StyledLink>
          </div>
        ) : null}

        {atiraLink?.map?.value ? (
          <div
            className={`${previewMode === TemplatePreviewMode.DESKTOP ? 'col-6' : 'col-12'} ${previewMode !== TemplatePreviewMode.MOBILE ? 'gx-1' : 'gx-0'} gy-1`}
          >
            <StyledLink
              href={
                atiraLink.map?.value.startsWith('http')
                  ? atiraLink.map.value
                  : `https://${atiraLink.map.value}`
              }
              style={
                showCustomStyles
                  ? getCustomizableButtonStyles(atiraLink.styles?.button)
                  : undefined
              }
              className="social-link gap-2 py-2 px-1 w-100 d-flex align-items-center justify-content-center text-decoration-none"
            >
              <div className="d-flex align-items-center justify-content-center">
                <i className="fa-solid fa-location-dot"></i>
              </div>
              <p className="m-0 text-truncate">{atiraLink.map?.label}</p>
            </StyledLink>
          </div>
        ) : null}

        {atiraLink?.phone?.value ? (
          <div
            className={`${previewMode === TemplatePreviewMode.DESKTOP ? 'col-6' : 'col-12'} ${previewMode !== TemplatePreviewMode.MOBILE ? 'gx-1' : 'gx-0'} gy-1`}
          >
            <StyledLink
              href={`tel:${atiraLink.phone.value}`}
              style={
                showCustomStyles
                  ? getCustomizableButtonStyles(atiraLink.styles?.button)
                  : undefined
              }
              className="social-link gap-2 py-2 px-1 w-100 d-flex align-items-center justify-content-center text-decoration-none"
            >
              <div className="d-flex align-items-center justify-content-center">
                <i className="fa-solid fa-phone"></i>
              </div>
              <p className="m-0 text-truncate">{atiraLink.phone?.label}</p>
            </StyledLink>
          </div>
        ) : null}

        {atiraLink?.customLinks?.map((link, index) =>
          link.label?.length && link.value?.length ? (
            <div
              className={`${previewMode === TemplatePreviewMode.DESKTOP ? 'col-6' : 'col-12'} ${previewMode !== TemplatePreviewMode.MOBILE ? 'gx-1' : 'gx-0'} gy-1`}
              key={`${link}-${index}`}
            >
              <StyledLink
                href={
                  link.value.startsWith('http')
                    ? link.value
                    : `https://${link.value}`
                }
                style={
                  showCustomStyles
                    ? getCustomizableButtonStyles(atiraLink.styles?.button)
                    : undefined
                }
                className="social-link gap-2 py-2 px-1 w-100 d-flex align-items-center justify-content-center text-decoration-none"
              >
                <div className="d-flex align-items-center justify-content-center">
                  <i className="fa-solid fa-link"></i>
                </div>
                <p className="m-0 text-truncate">{link.label}</p>
              </StyledLink>
            </div>
          ) : null,
        )}
      </div>
    </div>
  );
};
